import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  Users, Building2, Calendar, Briefcase, TrendingUp,
  UserPlus, UserCheck, Clock, CalendarDays, DollarSign, Award,
  Target, ClipboardList, Settings, ArrowRight,
  GraduationCap, Shield, CheckCircle, AlertCircle,
  Layers, Coffee, Activity, BarChart3, PieChart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function EnhancedHRDashboard({ user, onLogout }) {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    departments: 0,
    presentToday: 0,
    absentToday: 0,
    onLeave: 0,
    pendingLeaves: 0,
    openPositions: 0,
    candidates: 0,
    attendanceRate: 0,
    newHires: 0,
    branches: 0,
    profiles: 0,
    grades: 0,
    leaveTypes: 0,
    holidays: 0
  });
  const [loading, setLoading] = useState(true);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentHires, setRecentHires] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, hrRes, holidaysRes, leavesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/dashboard/hr-summary`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/holidays/upcoming?limit=4`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/leaves?status=Pending`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/employees?limit=5`, { headers }).catch(() => ({ data: [] }))
      ]);

      const d = dashboardRes.data;
      const hr = hrRes.data;

      setStats({
        totalEmployees: d.total_employees || 0,
        departments: hr.departments || d.total_departments || 0,
        presentToday: d.present_today || 0,
        absentToday: d.absent_today || 0,
        onLeave: d.on_leave_today || 0,
        pendingLeaves: d.pending_leaves || 0,
        openPositions: d.open_positions || 0,
        candidates: d.total_candidates || 0,
        attendanceRate: d.attendance_percentage || 0,
        newHires: d.new_hires_this_month || 0,
        branches: hr.branches || 0,
        profiles: hr.profiles || 0,
        grades: hr.grades || 0,
        leaveTypes: hr.leave_types || 0,
        holidays: hr.holidays || 0
      });

      setUpcomingHolidays(Array.isArray(holidaysRes.data) ? holidaysRes.data : []);
      
      const leaves = Array.isArray(leavesRes.data) ? leavesRes.data : [];
      setPendingApprovals(leaves.slice(0, 5).map(l => ({
        type: 'Leave',
        name: l.employee_name || l.employee_id,
        detail: `${l.leave_type} - ${l.number_of_days} days`,
        id: l.id
      })));

      // Recent hires
      const employees = Array.isArray(employeesRes.data) ? employeesRes.data : [];
      setRecentHires(employees.slice(0, 4).map(e => ({
        name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'N/A',
        department: e.department || e.departments?.[0] || 'General',
        date: e.joining_date || e.created_at?.split('T')[0] || 'Recent',
        status: e.employee_status || 'Active'
      })));

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, bgColor, link }) => (
    <Link to={link || '#'} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${bgColor}15` }}>
          <Icon className="w-6 h-6" style={{ color: bgColor }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
        <ArrowRight className="w-5 h-5 text-gray-300" />
      </div>
    </Link>
  );

  const ModuleCard = ({ title, description, icon: Icon, link, color, count }) => (
    <Link to={link} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 border border-gray-100 group">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors">{title}</h3>
            {count !== undefined && (
              <span className="text-sm font-bold" style={{ color }}>{count}</span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>HRMS Dashboard</h1>
            <p className="text-gray-500">Human Resource Management System Overview</p>
          </div>
          <div className="flex gap-3">
            <Link to="/hr-settings" className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              HR Settings
            </Link>
            <Link to="/onboarding/new" className="px-4 py-2 text-white rounded-lg flex items-center gap-2" style={{ backgroundColor: '#2b3380' }}>
              <UserPlus className="w-4 h-4" />
              Add Employee
            </Link>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Employees"
            value={stats.totalEmployees}
            subtitle={`+${stats.newHires} this month`}
            icon={Users}
            bgColor="#2b3380"
            link="/employees"
          />
          <StatCard
            title="Present Today"
            value={stats.presentToday}
            subtitle={`${stats.attendanceRate}% rate`}
            icon={UserCheck}
            bgColor="#4caf50"
            link="/attendance"
          />
          <StatCard
            title="On Leave"
            value={stats.onLeave}
            subtitle={`${stats.pendingLeaves} pending`}
            icon={CalendarDays}
            bgColor="#ff9800"
            link="/leaves"
          />
          <StatCard
            title="Open Positions"
            value={stats.openPositions}
            subtitle={`${stats.candidates} candidates`}
            icon={Briefcase}
            bgColor="#7e57c2"
            link="/recruitment/jobs"
          />
          <StatCard
            title="Departments"
            value={stats.departments}
            icon={Building2}
            bgColor="#00897b"
            link="/org-structure/departments"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* HRMS Modules */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5" style={{ color: '#2b3380' }} />
              HRMS Modules
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ModuleCard
                title="Employee Management"
                description="View, add and manage employees"
                icon={Users}
                link="/employees"
                color="#2b3380"
                count={stats.totalEmployees}
              />
              <ModuleCard
                title="Attendance & Time"
                description="Track attendance and working hours"
                icon={Clock}
                link="/attendance"
                color="#4caf50"
                count={stats.presentToday}
              />
              <ModuleCard
                title="Leave Management"
                description="Apply and approve leaves"
                icon={CalendarDays}
                link="/leaves"
                color="#ff9800"
                count={stats.pendingLeaves}
              />
              <ModuleCard
                title="Payroll & Salary"
                description="Process payroll and salaries"
                icon={DollarSign}
                link="/payroll"
                color="#4caf50"
              />
              <ModuleCard
                title="Recruitment"
                description="Manage hiring pipeline"
                icon={Target}
                link="/recruitment/jobs"
                color="#7e57c2"
                count={stats.openPositions}
              />
              <ModuleCard
                title="Performance"
                description="Reviews and KPIs"
                icon={Award}
                link="/performance-reviews"
                color="#f44336"
              />
              <ModuleCard
                title="Training & Development"
                description="Learning programs"
                icon={GraduationCap}
                link="/training"
                color="#00bcd4"
              />
              <ModuleCard
                title="Organization Structure"
                description="Departments, grades, profiles"
                icon={Building2}
                link="/org-structure/departments"
                color="#795548"
                count={stats.departments}
              />
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Pending Approvals */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Pending Approvals
                </h2>
                <Link to="/approvals" className="text-sm text-indigo-600 hover:underline">View All</Link>
              </div>
              {pendingApprovals.length > 0 ? (
                <div className="space-y-3">
                  {pendingApprovals.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.detail}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                        {item.type}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                  <CheckCircle className="w-10 h-10 mb-2" />
                  <p className="text-sm">All caught up!</p>
                </div>
              )}
            </div>

            {/* Recent Hires */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-green-500" />
                  Recent Hires
                </h2>
                <Link to="/employees" className="text-sm text-indigo-600 hover:underline">View All</Link>
              </div>
              {recentHires.length > 0 ? (
                <div className="space-y-3">
                  {recentHires.map((hire, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm" style={{ backgroundColor: '#2b3380' }}>
                        {hire.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{hire.name}</p>
                        <p className="text-xs text-gray-500">{hire.department}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        hire.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {hire.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                  <Users className="w-10 h-10 mb-2" />
                  <p className="text-sm">No recent hires</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Holidays & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Holidays */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-500" />
                Upcoming Holidays
              </h2>
              <Link to="/holiday-calendar" className="text-sm text-indigo-600 hover:underline">View Calendar</Link>
            </div>
            {upcomingHolidays.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {upcomingHolidays.map((holiday, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 flex flex-col items-center justify-center bg-indigo-100 rounded-lg">
                      <span className="text-xs font-medium text-indigo-600">
                        {new Date(holiday.date).toLocaleDateString('en', { month: 'short' })}
                      </span>
                      <span className="text-lg font-bold text-indigo-700">
                        {new Date(holiday.date).getDate()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{holiday.name}</p>
                      <p className="text-xs text-gray-500">{holiday.type || 'Holiday'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <Coffee className="w-12 h-12 mb-2" />
                <p className="text-sm">No upcoming holidays</p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link to="/onboarding/new" className="flex flex-col items-center p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors">
                <UserPlus className="w-6 h-6 text-indigo-600 mb-2" />
                <span className="text-xs font-medium text-gray-700 text-center">Add Employee</span>
              </Link>
              <Link to="/recruitment/jobs" className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
                <Briefcase className="w-6 h-6 text-purple-600 mb-2" />
                <span className="text-xs font-medium text-gray-700 text-center">Post Job</span>
              </Link>
              <Link to="/payroll" className="flex flex-col items-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                <DollarSign className="w-6 h-6 text-green-600 mb-2" />
                <span className="text-xs font-medium text-gray-700 text-center">Run Payroll</span>
              </Link>
              <Link to="/roles" className="flex flex-col items-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                <Shield className="w-6 h-6 text-blue-600 mb-2" />
                <span className="text-xs font-medium text-gray-700 text-center">Manage Roles</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default EnhancedHRDashboard;
